import { useCallback, useEffect, useState } from 'react';
import { type ApiError, isAbortError, toApiError } from './client';

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  /** True only until the first response lands. Later polls refresh silently. */
  loading: boolean;
  lastUpdatedAt: number | null;
}

export interface PollResult<T> extends AsyncState<T> {
  refresh: () => void;
}

export type Fetcher<T> = (signal: AbortSignal) => Promise<T>;

/**
 * Polls `fetcher` on an interval.
 *
 * The fetcher must be memoised with `useCallback` by the caller: its identity is
 * what tells this hook that the query changed and the timer has to restart.
 *
 * Previous data is kept while a refresh is in flight so the table does not blank
 * out every two seconds, and overlapping requests are suppressed so a slow API
 * cannot build up a queue of pending polls.
 */
export function usePolling<T>(fetcher: Fetcher<T>, intervalMs: number, enabled = true): PollResult<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    error: null,
    loading: true,
    lastUpdatedAt: null,
  });

  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let inFlight = false;
    let cancelled = false;

    const run = async (): Promise<void> => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const data = await fetcher(controller.signal);
        if (cancelled) return;
        setState({ data, error: null, loading: false, lastUpdatedAt: Date.now() });
      } catch (error) {
        if (cancelled || isAbortError(error)) return;
        setState((prev) => ({ ...prev, error: toApiError(error), loading: false }));
      } finally {
        inFlight = false;
      }
    };

    // Always fetch once, even when polling is paused, so the view has content.
    void run();

    if (!enabled) {
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    const timer = window.setInterval(() => {
      // No point polling a tab nobody is looking at.
      if (document.hidden) return;
      void run();
    }, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      controller.abort();
    };
  }, [fetcher, intervalMs, enabled, nonce]);

  return { ...state, refresh };
}

/** One-shot load with the same state shape, refetched via `refresh()`. */
export function useAsync<T>(fetcher: Fetcher<T>): PollResult<T> {
  return usePolling(fetcher, 0, false);
}

/** Ticking clock so relative timestamps and countdowns stay honest. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);

  return now;
}

/** Keeps a text filter from firing a request on every keystroke. */
export function useDebounced<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
